// Sign out as a ROUTE, not only as a button.
//
// Every sign-out control on screen is a click handler on an element that has to
// be visible, on top, and inside the rail's current display mode to work at all.
// A route removes all of that: /ops/logout signs you out even if the chrome is
// unclickable, and the buttons become plain links into it rather than bespoke
// handlers that can each break in their own way.
//
// IT MUST BE INSTANT. Two things made it anything but:
//
//   1. It awaited the POST before redirecting, so you watched a card for as long
//      as the round trip took — seconds, against a remote database. The request
//      is now fired with `keepalive`, the one fetch mode guaranteed to complete
//      after its document goes away, including the Set-Cookie that clears the
//      session cookie.
//   2. It then redirected with window.location, a FULL DOCUMENT LOAD that
//      re-downloads and re-parses a 4.7MB bundle — about five seconds of staring
//      at the page you were trying to leave, since a browser keeps showing the
//      old document until the new one renders. The SPA is already running, so
//      that work bought nothing. We navigate through the router instead.
//
// What a full reload DID do for free was destroy the React tree, which closed
// the SIP socket and dropped the agent's registration. A client-side navigation
// does not — SipProvider deliberately sits above the router so a phone survives
// page changes — so signing out now tears the phone down explicitly. Without
// that, an agent who signs off stays registered and the next person to sit down
// takes their calls.
//
// The trade-off in not awaiting: a failed sign-out cannot be reported here. It
// is not silent — the session would still be live, and App's auth probe bounces
// an authed visitor back into the product rather than showing the login form.

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { opsFetch, setActiveTenantSlug } from "../api";
import { invalidateTenantsCache } from "../components/Shell";
import { clearSavedReg, useSoftphone } from "./voice/SipContext";

export default function Logout({ onSignedOut }: { onSignedOut: () => void }) {
  const navigate = useNavigate();
  const sip = useSoftphone();
  // StrictMode invokes effects twice in development and this one has a side
  // effect. The audit log carries pairs of auth.logout rows 115ms apart, which
  // is a double-fire rather than a double-click.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // Fired, not awaited.
    void opsFetch("/ops/auth/logout", { method: "POST", keepalive: true }).catch(
      (err: unknown) => console.warn("[ops] sign out request failed:", err),
    );

    // Stop taking calls. See the note above on why this is now explicit.
    try { sip.disconnect(); } catch { /* never block the redirect */ }

    // Drop the workspace scope so the next user doesn't inherit a slug for a
    // workspace they don't belong to, and the SIP credential with it — that one
    // is stored per BROWSER, so leaving it behind hands over this agent's
    // extension along with their calls. Best-effort: failing to tidy up local
    // state is not a reason to keep someone signed in.
    try { setActiveTenantSlug(null); } catch { /* never block the redirect */ }
    try { clearSavedReg(); } catch { /* never block the redirect */ }
    // And Shell's module-level workspace list. Signing out no longer reloads
    // the document, so that cache outlives the session: without this the next
    // account to sign in on this tab is seeded with the previous user's
    // workspaces in the switcher.
    try { invalidateTenantsCache(); } catch { /* never block the redirect */ }

    // Tell App we are anonymous BEFORE navigating. /login renders
    // <Navigate to="/" replace /> for as long as auth state reads "authed", so
    // without this the router would bounce straight back into the product.
    onSignedOut();

    // replace, not push: this route is a transition, not a place. Leaving it in
    // history means Back lands on a page that immediately signs you out again.
    navigate("/login", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rendered for a frame at most.
  return <div style={{ minHeight: "100vh", background: "var(--bg, #0e1116)" }} />;
}
